import React, { useState, useEffect } from 'react';

const Step_SelectCriteria = ({ data, onDataChange, evaluationData }) => {
  // Get default criteria type based on evaluation type
  const getDefaultCriteriaType = () => {
    if (data?.evaluationType === 'Job') return 'VSJob';
    if (data?.evaluationType === 'Talent') return 'VSResume';
    if (data?.evaluationType === 'Custom') return 'VSSkillset';
    return 'VSJob';
  };

  // Local state for form data
  const [formData, setFormData] = useState({
    type: getDefaultCriteriaType(),
    extra_criteria_instructions: '',
    vsskillset_user_criteria_input: '',
    is_default_evaluation: false,
    generate_directly: false,
    ...data // Override with any passed data
  });

  // Send initial data to parent on component mount
  useEffect(() => {
    onDataChange?.(formData);
  }, []); // Empty dependency array means this runs once on mount

  // Update criteria type when evaluation type changes
  useEffect(() => {
    const newCriteriaType = getDefaultCriteriaType();
    if (newCriteriaType !== formData.type) {
      const newData = { ...formData, type: newCriteriaType };
      setFormData(newData);
      onDataChange?.(newData);
    }
  }, [data?.evaluationType]); // Run when evaluationType changes

  // Handle form changes and notify parent
  const handleChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    onDataChange?.(newData);
  };

  // Get available criteria options based on evaluation type
  const getCriteriaOptions = () => {
    if (data?.evaluationType === 'Job') {
      return [
        { label: 'Job Requirements', value: 'VSJob', subtitle: '' },
        { label: 'Custom', value: 'VSSkillset', subtitle: '' }
      ];
    } else if (data?.evaluationType === 'Talent') {
      return [
        { label: 'Talent Profile', value: 'VSResume', subtitle: '' },
        { label: 'Custom', value: 'VSSkillset', subtitle: '' }
      ];
    } else if (data?.evaluationType === 'Custom') {
      return [
        { label: 'Custom', value: 'VSSkillset', subtitle: '' }
      ]; // Only custom option for Custom
    }
    return [];
  };

  const criteriaOptions = getCriteriaOptions();
  const showCriteriaSelection = true; // Always show criteria selection

  return (
    <div className="grid h-full relative" style={{ gridTemplateColumns: '66.67% 33.33%' }}>
      {/* Left Column - Form with blue background, scrollable */}
      <div 
        className="overflow-y-auto" 
        style={{ 
          height: 'calc(100% - 88px)', // Account for navigation bar height
          backgroundColor: 'var(--color-brand-blue-light)',
          WebkitOverflowScrolling: 'touch', // Smooth iOS-style scrolling
          scrollBehavior: 'smooth', // Smooth scrolling behavior
          overscrollBehaviorY: 'auto' // Enable elastic bounce scrolling
        }}
      >
        <div className="p-8 space-y-6">
          {/* Centered Title */}
          <div className="text-center mb-8">
            <h2 className="text-4xl font-regular text-gray-900 mb-2 mt-4">Generate Evaluation Criteria</h2>
            <p className="text-md text-gray-600 mb-8">
              <span className="font-medium">{data?.selectedJob?.title || data?.selectedTalent?.name || 'Custom Evaluation'}</span>
              {data?.selectedJob?.company && <span className="text-gray-700"> at {data.selectedJob.company}</span>}
            </p>
            
            <p className="text-2xl text-black-600">
              What data should the evaluation be based on?
            </p>
          </div>

          {/* Criteria Selection - Styled like Step_Start */}
          <div className="max-w-2xl">
            
            {/* Unified Container with Tabs and Fields */}
            <div className="border-2 border-gray-200 rounded-lg bg-white overflow-visible">
              {/* Tab Header */}
              <div className="flex space-x-1 bg-gray-100 p-1">
                {criteriaOptions.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => handleChange('type', type.value)}
                    className={`flex-1 py-2 px-4 text-md font-medium rounded-md transition-colors ${
                      formData.type === type.value
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    <div className="text-center">
                      <div>{type.label}</div>
                      <div className="text-xs opacity-75">{type.subtitle}</div>
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Form Fields */}
              <div className="p-4 space-y-6">

                
                {/* Conditional Fields based on criteria type */}
                {(formData.type === 'VSJob' || formData.type === 'VSResume') && (
                  <div>
                    <label className="block text-base font-medium text-gray-700 mb-2">
                      Extra Criteria Input <span className="text-gray-500">(optional)</span>
                    </label>
                    <textarea
                      value={formData.extra_criteria_instructions}
                      onChange={(e) => handleChange('extra_criteria_instructions', e.target.value)}
                      placeholder="Additional data to include in the criteria (e.g. skills, expertise, etc)"
                      className="w-full h-32 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-white placeholder-gray-500 transition-colors"
                    />
                  </div>
                )}

                {formData.type === 'VSSkillset' && (
                  <div>
                    <label className="block text-base font-medium text-gray-700 mb-2">
                      Seed The Criteria <span className="text-gray-500">(optional)</span>
                    </label>
                    <textarea
                      value={formData.vsskillset_user_criteria_input}
                      onChange={(e) => handleChange('vsskillset_user_criteria_input', e.target.value)}
                      placeholder="I want to verify a Mobile Developer for SKILL1, SKILL2 and SKILL3, and understand if they have experience building high-security apps. I would like to ask specifically QUESTION1, QUESTION2 and QUESTION3."
                      className="w-full h-48 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-white placeholder-gray-500 transition-colors"
                    />
                  </div>
                )}
              </div>
            </div>
            
            {/* Generate Directly Checkbox - Below the tabs box */}
            <div className="mt-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="generate_directly"
                  checked={formData.generate_directly || false}
                  onChange={(e) => handleChange('generate_directly', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label htmlFor="generate_directly" className="ml-3 text-base font-medium text-gray-700">
                  Generate Evaluation Directly (Skip Criteria Confirm)
                </label>
              </div>
              <p className="mt-1 text-sm text-red-500 ml-7">
                Will use {' '}
                <a
                  href="/settings"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-red-700"
                >
                  Default Evaluation Settings
                </a>
                {' '}(e.g. breadth, time, number of questions, etc).
              </p>
            </div>
          </div>

          {/* For Custom, show a simple message */}
          {!showCriteriaSelection && (
            <div className="border-2 border-gray-200 rounded-lg bg-white overflow-visible p-6">
              <p className="text-sm text-gray-600 italic">
                Custom evaluation criteria will be configured in the next step
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Right Column - Explainer content with vertical center alignment */}
      <div className="bg-white flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          {/* Centered Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white text-lg font-bold">i</span>
            </div>
          </div>
          
          {/* Main Heading */}
          <h3 className="text-xl font-semibold text-gray-900 mb-6 leading-tight">
            The Criteria sets the content of the Evaluation. 
          </h3>
          
          {/* Subtext */}
          <p className="text-base text-gray-600 leading-relaxed">
            Review and confirm criteria in the next step.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Step_SelectCriteria;
